import { useEffect, useState } from 'react';
import { LogOut, ShieldOff, ShieldCheck, UserPlus, CreditCard } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/Modal';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Assignable roles — mirrors App\Enums\UserRole on the backend. */
const ROLES = ['candidate', 'agent', 'admin', 'superadmin'] as const;
type Role = (typeof ROLES)[number];

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  locale: string;
  role: string;
  status: string;
  created_at: string;
}
interface Page<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
}
interface Plan {
  id: string;
  code: string;
  name: string;
  currency: string;
  amount_minor: number;
  interval_months: number;
}
interface CurrentSubscription {
  id: string;
  status: string;
  provider: string;
  is_manual: boolean;
  is_premium_active: boolean;
  current_period_end: string | null;
  plan: Plan | null;
}

const fieldClass =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function UsersPage() {
  const currentUser = useAuth((s) => s.user);
  const isSuperadmin = currentUser?.role === 'superadmin';
  const canManageRoles = isSuperadmin;

  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | Role>('');
  const [page, setPage] = useState<Page<UserRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [planUser, setPlanUser] = useState<UserRow | null>(null);

  function load() {
    const params = new URLSearchParams();
    if (q.trim().length > 0) params.set('q', q.trim());
    if (roleFilter !== '') params.set('role', roleFilter);
    api<Page<UserRow>>(`/admin/users${params.toString() !== '' ? '?' + params.toString() : ''}`)
      .then(setPage)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Load failed'));
  }
  // Deliberate run-once-on-mount fetch; the search box triggers an
  // explicit reload, not via the dependency array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  async function action(id: string, route: string) {
    setBusy(id + route);
    try {
      await api(`/admin/users/${id}/${route}`, { method: 'POST' });
      load();
    } catch {
      setError('Action failed.');
    } finally {
      setBusy(null);
    }
  }

  async function changeRole(id: string, role: Role) {
    setBusy(id + 'role');
    setError(null);
    try {
      await api(`/admin/users/${id}/role`, { method: 'POST', json: { role } });
      load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Only superadmins can change roles.'
          : 'Role change failed.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Search, create, suspend, restore, force-logout, change role, manage plan.
          </p>
        </div>
        {isSuperadmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" /> New user
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Email or display name…"
              className="max-w-sm"
            />
            <select
              aria-label="Filter by role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as '' | Role)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">All roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Results {page !== null && `(${page.total})`}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="py-2 font-bold">User</th>
                <th className="py-2 font-bold">Role</th>
                <th className="py-2 font-bold">Status</th>
                <th className="py-2 font-bold">Created</th>
                <th className="py-2 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {page?.data.map((u) => (
                <tr key={u.id}>
                  <td className="py-3">
                    <p className="font-bold">{u.display_name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="py-3">
                    {canManageRoles ? (
                      <select
                        aria-label={`Role for ${u.display_name}`}
                        value={u.role}
                        disabled={busy !== null || u.id === currentUser?.id}
                        title={
                          u.id === currentUser?.id ? "You can't change your own role" : undefined
                        }
                        onChange={(e) => void changeRole(u.id, e.target.value as Role)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge variant="secondary">{u.role}</Badge>
                    )}
                  </td>
                  <td className="py-3">
                    <Badge variant={u.status === 'active' ? 'default' : 'secondary'}>
                      {u.status}
                    </Badge>
                  </td>
                  <td className="py-3 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                  <td className="py-3">
                    <div className="flex justify-end gap-2">
                      {u.status === 'active' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void action(u.id, 'suspend')}
                        >
                          <ShieldOff className="mr-1 h-4 w-4" /> Suspend
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void action(u.id, 'restore')}
                        >
                          <ShieldCheck className="mr-1 h-4 w-4" /> Restore
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void action(u.id, 'force-logout')}
                      >
                        <LogOut className="mr-1 h-4 w-4" /> Force logout
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPlanUser(u)}>
                        <CreditCard className="mr-1 h-4 w-4" /> Plan
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {page?.data.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No users.</p>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
      {planUser !== null && <ManagePlanModal user={planUser} onClose={() => setPlanUser(null)} />}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('candidate');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/admin/users', {
        method: 'POST',
        json: { email, display_name: displayName, role, password },
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError(firstValidationError(err) ?? 'Please check the fields and try again.');
      } else if (err instanceof ApiError && err.status === 403) {
        setError('Only superadmins can create users.');
      } else {
        setError('Create failed.');
      }
      setBusy(false);
    }
  }

  return (
    <Modal title="Create user" onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Email</span>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Display name</span>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className={fieldClass}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Initial password</span>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <span className="text-xs text-muted-foreground">
            The user can change it after signing in.
          </span>
        </label>

        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ManagePlanModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const [sub, setSub] = useState<CurrentSubscription | null | undefined>(undefined);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [planId, setPlanId] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function loadSub() {
    api<{ data: CurrentSubscription | null }>(`/admin/users/${user.id}/subscription`)
      .then((r) => setSub(r.data))
      .catch(() => setSub(null));
  }

  useEffect(() => {
    loadSub();
    api<{ data: Plan[] }>('/admin/plans')
      .then((r) => setPlans(r.data))
      .catch(() => setPlans([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  async function grant(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/users/${user.id}/subscription/grant`, {
        method: 'POST',
        json: { plan_id: planId, ends_at: endsAt },
      });
      setSub(undefined);
      loadSub();
    } catch (err) {
      setError(
        err instanceof ApiError ? (firstValidationError(err) ?? err.message) : 'Grant failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!window.confirm('Revoke this comp plan?')) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/users/${user.id}/subscription/revoke`, { method: 'POST' });
      setSub(undefined);
      loadSub();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  }

  const canRevoke = sub != null && sub.is_manual && sub.status !== 'canceled';

  return (
    <Modal title={`Plan — ${user.display_name}`} onClose={onClose}>
      <div className="space-y-5">
        <section>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Current subscription
          </h3>
          {sub === undefined && <div className="h-16 animate-pulse rounded-2xl bg-muted" />}
          {sub === null && <p className="text-sm text-muted-foreground">No subscription.</p>}
          {sub != null && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-bold">{sub.plan?.name ?? '—'}</span>
              <Badge variant={sub.is_premium_active ? 'default' : 'secondary'}>{sub.status}</Badge>
              <Badge variant="secondary">{sub.provider}</Badge>
              {sub.current_period_end !== null && (
                <span className="text-muted-foreground">
                  until {formatDate(sub.current_period_end)}
                </span>
              )}
            </div>
          )}
          {canRevoke && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={busy}
              onClick={() => void revoke()}
            >
              Revoke comp plan
            </Button>
          )}
          {sub != null && !sub.is_manual && (
            <p className="mt-2 text-xs text-muted-foreground">
              This is a paid subscription — manage it through the billing provider.
            </p>
          )}
        </section>

        <section className="border-t border-border pt-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Grant comp plan
          </h3>
          <form className="space-y-3" onSubmit={grant}>
            <label className="block space-y-1.5">
              <span className="text-sm font-bold">Plan</span>
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className={fieldClass}
                required
              >
                <option value="">Select a plan…</option>
                {plans?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-bold">Ends on</span>
              <Input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                required
              />
            </label>
            {error !== null && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button type="submit" disabled={busy || planId === '' || endsAt === ''}>
                {busy ? 'Saving…' : 'Grant plan'}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </Modal>
  );
}

/** Pull the first field error out of a Laravel 422 body. */
function firstValidationError(err: ApiError): string | null {
  const body = err.body;
  if (typeof body === 'object' && body !== null && 'errors' in body) {
    const errors = (body as { errors: Record<string, string[]> }).errors;
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }
  return null;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
